import { Button, IconButton, Tab, Tabs } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import React, { useEffect, useReducer, useState } from 'react';
//
import { useDispatch, useSelector } from 'react-redux';
import { AMENITIES_INFO_TYPES, ASSET_AMENITIES } from '../../../../../constants/AmenityConst';
import ContactInformation from '../../../../../shared/contact-information/ContactInformation';
import CustomMultiSelect from '../../../../../shared/form-controls/custom-multi-select/Custom-Multi-Select';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import { getProcessedImages } from '../../../../../shared/image-upload/ImageProcessor';
import ImageUpload from '../../../../../shared/image-upload/ImageUpload';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import { formDataValidate } from '../../../../../utils/formUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { createNewAmenity, updateAmenityDetails } from '../../../../../utils/PropertyAPIUtils';
import {
  allAmenityCategoriesSelector,
  allHotelsSelector,
  fetchAllAmenities,
} from '../../../../property/propertySlice';
import './Amenities-Create.scss';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import KeyValuePairList from '../../../../../shared/key-value-pair-list/KeyValuePairList';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';

const AmenitiesCreate = (props) => {
  const dispatch = useDispatch();
  const originalHotels = useSelector(allHotelsSelector);
  const { toggleDrawer, amenity, hotelId, showLoader = () => {} } = props;
  const [tabValue, setTabValue] = useState(0);
  const [isTabInValid, setIsTabInValid] = useState(true);
  const [rawImages, setRawImages] = useState([]);
  const amenityCategories = useSelector(allAmenityCategoriesSelector);
  const [loading, setLoading] = useState(false);
  const [contactInfoList, setContactInfoList] = useState([]);

  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    categoryId: {
      value: '',
      fieldName: 'Category',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    name: {
      value: '',
      fieldName: 'Name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    description: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    highlights: {
      value: '',
      fieldName: 'Highlights (Separated by commas)',
      errors: {},
      validations: [],
      tab: 0,
    },
    customAttributes: {
      value: [],
      fieldName: 'Custom Attributes',
      errors: {},
      validations: [],
      tab: 0,
    },
    images: {
      value: [],
      fieldName: 'Images',
      errors: {},
      validations: [],
      tab: 2,
    },
  });

  useEffect(() => {
    if (amenity) {
      const currFields = { ...formData };
      for (let field in currFields) {
        currFields[field].value = amenity[field];
      }
      currFields.images.value = amenity.images || [];
      currFields.categoryId.value = amenity.categoryIds || [];
      currFields.customAttributes.value = amenity?.customAttributes || [];
      const infoList = [...amenity?.information] || [];
      setContactInfoList(infoList);
      setFormData(currFields);
    }
  }, [amenity]);

  const validate = (tabValue) => {
    const updatedFields = {};
    // If tabValue is given, filter out the form fields in current tab
    const currTabFromData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tabValue),
    );

    const isValid = formDataValidate(currTabFromData, updatedFields);
    setFormData(updatedFields);
    return isValid;
  };

  const onNextClick = () => {
    if (validate(tabValue)) {
      setTabValue(tabValue + 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validate(0)) {
      dispatch(stepUpLoaderCount());
      toggleDrawer('', false, '');
      const selectedLanguage = decryptLocalStorage('lang');
      const language = selectedLanguage !== 'en' ? selectedLanguage : '';
      setLoading(true);
      const payload = {};

      for (let field in formData) {
        payload[field] = formData[field]?.value;
      }

      payload.customAttributes = formData?.customAttributes?.value;

      payload.information = contactInfoList?.map((info, i) => {
        return { ...info, index: i };
      });

      try {
        // Process the image upload first and set the response to main payload
        payload.images = await getProcessedImages(
          rawImages,
          originalHotels.find((hotel) => hotel.id === hotelId).groupId,
          ASSET_AMENITIES,
          hotelId,
        );

        if (amenity) {
          payload.id = amenity?.id;
          await updateAmenityDetails(payload, hotelId, language);
          notificationManager('success', 'Hotel Compendium updated successfully');
        } else {
          await createNewAmenity(payload, hotelId, language);
          notificationManager('success', 'Hotel Compendium created successfully');
        }

        dispatch(fetchAllAmenities(hotelId));
        setLoading(false);
      } catch (error) {
        setLoading(false);
        if (error?.graphQLErrors) {
          notificationManager('danger', error?.graphQLErrors?.[0]?.message);
        } else {
          amenity
            ? notificationManager('danger', 'Hotel Compendium updation failed')
            : notificationManager('danger', 'Hotel Compendium creation failed');
        }
      } finally {
        dispatch(stepDownLoaderCount());
      }
    } else {
      notificationManager('danger', 'Enter all required fields');
      setTabValue(0);
      if (!validate(0)) {
        setTabValue(0);
      }
      if (!validate(1)) {
        setTabValue(1);
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    if (newValue < tabValue) {
      setTabValue(newValue);
    } else if (validate(tabValue)) {
      setTabValue(newValue);
    }
  };

  const onImageListUpdated = (imgObjArr) => {
    setRawImages(imgObjArr);
  };

  const onUploadedImageRemoval = (index) => {
    const images = [...formData.images.value];
    images.splice(index, 1);
    setFormData({ images });
  };

  const onCancel = (e) => {
    toggleDrawer('', false, '');
  };

  useEffect(() => {
    const currTabFromData = Object.fromEntries(
      Object.entries(formData).filter(([key, field]) => field.tab === tabValue),
    );
    const isValid = formDataValidate(currTabFromData, false);
    setIsTabInValid(!isValid);
  }, [formData]);

  const renderCustomDisplay = (selectedValue) => {
    const displayName = selectedValue?.map(
      (itemData) => amenityCategories?.find((category) => category.id === itemData)?.name,
    );
    return <span>{displayName.join(', ')}</span>;
  };

  return (
    <form className='category-create' noValidate>
      <div className='container pt-4 d-flex align-items-end flex-column '>
        <div className='row px-3 w-100 mt-4'>
          <div className='col-md-6'>
            <span className='page-title'>
              {amenity ? 'Edit Hotel Compendium' : 'Add Hotel Compendium'}
            </span>
          </div>
          <div className='col-md-5'>
            <Tabs
              value={tabValue}
              variant='fullWidth'
              indicatorColor='primary'
              onChange={handleTabChange}
              aria-label='amenities-add-tabs'
              textColor='primary'
              className='property-tabs'
            >
              <Tab label='Details' />
              <Tab label='Information' />
              <Tab label='Media' />
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton aria-label='delete' onClick={(e) => onCancel(e)}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        {tabValue === 0 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <h4 className='tab-title'>Details</h4>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <CustomMultiSelect
                  item={formData.categoryId}
                  options={amenityCategories.map((category) => ({
                    id: category.id,
                    name: category.name,
                  }))}
                  hideNullOption={amenityCategories.length}
                  handleInputChange={(e) => {
                    setFormData({ categoryId: e });
                  }}
                  labelField='name'
                  valueField='id'
                  isCustomDisplay={true}
                  renderCustomDisplay={renderCustomDisplay}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.name}
                  handleInputChange={(e) => {
                    setFormData({ name: e });
                  }}
                  maxLength={100}
                  multiline={true}
                  endAdornment={true}
                  characterCount={true}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-12 mb-3'>
                <CustomTextField
                  item={formData.description}
                  handleInputChange={(e) => {
                    setFormData({ description: e });
                  }}
                  multiline={true}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-12 mb-3'>
                <CustomTextField
                  item={formData.highlights}
                  handleInputChange={(e) => {
                    setFormData({ highlights: e });
                  }}
                  multiline={true}
                />
              </div>
            </div>
            <div>
              <KeyValuePairList
                item={formData?.customAttributes}
                onListChange={(e) => {
                  setFormData({ customAttributes: e });
                }}
              />
            </div>
          </div>
        )}
        {tabValue === 1 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='col-md-12 mb-3'>
              <h4 className='tab-title '>Information</h4>
              <ContactInformation
                informationTypes={AMENITIES_INFO_TYPES}
                setContactInfoList={setContactInfoList}
                contactInfoList={contactInfoList}
                infoFieldKey='field'
                infoValueKey='value'
              />
            </div>
          </div>
        )}
        {tabValue === 2 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='col-md-12 mb-3'>
              <ImageUpload
                uploadedImages={formData.images.value}
                imgList={rawImages}
                onImageListUpdated={onImageListUpdated}
                onUploadedImageRemoval={onUploadedImageRemoval}
                onImagesLoading={(status) => {
                  setLoading(status);
                }}
                enabledMediaTabs={['image']}
              />
            </div>
          </div>
        )}
        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            {tabValue < 2 ? (
              <Button
                variant='contained'
                color='primary'
                onClick={onNextClick}
                size='large'
                className='me-3'
                disabled={isTabInValid}
              >
                Next
              </Button>
            ) : (
              <Button
                disabled={loading}
                onClick={handleSubmit}
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
              >
                {amenity ? 'Update' : 'Add'}
              </Button>
            )}
            <Button
              disabled={loading}
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={(e) => onCancel(e)}
            >
              Cancel
            </Button>
          </div>
          {loading && <div className='pt-4 col-md-12'>{<LinearProgressBar />}</div>}
        </div>
      </div>
    </form>
  );
};
export default AmenitiesCreate;
